import AccountBalance from '@mui/icons-material/AccountBalance';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Chat from '@mui/icons-material/Chat';
import DirectionsCarOutlined from '@mui/icons-material/DirectionsCarOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import LocationOn from '@mui/icons-material/LocationOn';
import Phishing from '@mui/icons-material/Phishing';
import Pool from '@mui/icons-material/Pool';
import Rule from '@mui/icons-material/Rule';
import Sailing from '@mui/icons-material/Sailing';
import ScubaDiving from '@mui/icons-material/ScubaDiving';
import Search from '@mui/icons-material/Search';
import { GridLegacy as Grid, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PageWithHeader from 'components/common/PageWithHeader';
import { usePageContext } from 'pages/usePageContext';
import React, { Profiler, useEffect, useState } from 'react';
import { BodyText, getDynamicBranding, styles } from 'theme/theme';

import SailBoat from '../../assets/icons/SailBoat';
import Stars from '../../assets/icons/Stars';
import {
  HOME_LISTING_LIMIT,
  LISTING_CARD_IMAGE_WIDTHS,
  listingVisibilitySx,
} from '../../config/homePage';
import { appConfig } from '../../config/runtimeConfig';
import { useRouter } from '../../hooks/useRouter';
import {
  BoatWithRelations,
  ExperienceWithRelations,
  PropertyWithRelations,
  useGetBoatsQuery,
  useGetExperiencesQuery,
  useGetPropertiesQuery,
  useGetVehiclesQuery,
  VehicleWithRelations,
} from '../../redux/ellipsisApi';
import { SsrTimer } from '../../server/timing';
import { heroImageSizes, heroImageUrl } from '../../utils/heroImage';
import FeatureList from '../common/FeatureList';
import FeatureListItem from '../common/FeatureListItem';
import HomePageCard from '../common/HomePageCard';
import ListingCard from '../common/ListingCard';
import { ListingCardSkeletonGrid } from '../common/ListingCardSkeleton';
import PageMeta from '../common/PageMeta';
import SeeAllCard from '../common/SeeAllCard';
import HomeSearch from '../search/HomeSearch';
import { withSsrTiming } from '../utils/withSsrTiming';
interface ContentWrapperInterface {
  maxwidth?: number | string | false;
}

const ContentWrapper = styled('section')<ContentWrapperInterface>((props) => ({
  margin: '0 auto',
  maxWidth: props.maxwidth ? `${props.maxwidth}px` : '',
  width: '100%',
}));

type InitialData = {
  boats?: BoatWithRelations[];
  experiences?: ExperienceWithRelations[];
  vehicles?: VehicleWithRelations[];
  properties?: PropertyWithRelations[];
};

type HomeListing =
  | BoatWithRelations
  | ExperienceWithRelations
  | VehicleWithRelations
  | PropertyWithRelations;

type ListingSection = {
  id: string;
  heading: string;
  icon: React.ReactElement;
  bookableType: 'Boat' | 'Experience' | 'Vehicle' | 'Property';
  searchTab: string;
  href: string;
  ctaLabel: string;
  listings?: HomeListing[];
  isLoading: boolean;
};

const HomePage: React.FC<{ initialData?: InitialData }> = ({ initialData }) => {
  const router = useRouter();
  const pageContext = usePageContext();
  const timer: SsrTimer =
    typeof window === 'undefined' && pageContext
      ? (pageContext as any).ssrTimer || new SsrTimer()
      : null;
  if (pageContext) {
    (pageContext as any).ssrTimer = timer;
  }
  // Load a small, stable set on first paint (homepage shows previews only)
  const INITIAL_LIMIT = HOME_LISTING_LIMIT;
  // Compact React Profiler logger (dev-only friendly). Threshold keeps noise down.
  function onRender(
    id: string,
    phase: 'mount' | 'update' | 'nested-update',
    actualDuration: number,
    baseDuration: number,
    startTime: number,
    commitTime: number
  ) {
    const THRESHOLD_MS = 8; // only log meaningful work
    if (actualDuration >= THRESHOLD_MS) {
      console.warn(
        `[REACT][PROFILE] id=${id} phase=${phase} actual=${actualDuration.toFixed(
          1
        )} base=${baseDuration.toFixed(1)} start=${startTime.toFixed(1)} commit=${commitTime.toFixed(
          1
        )}`
      );
    }
  }

  timer?.start('home:fetchData');
  const { data: allBoats, isLoading: allBoatsIsLoading } = useGetBoatsQuery({
    limit: INITIAL_LIMIT,
  });
  const { data: allExperiences, isLoading: allExperiencesIsLoading } =
    useGetExperiencesQuery({
      limit: INITIAL_LIMIT,
    });
  const { data: allVehicles, isLoading: allVehiclesIsLoading } =
    useGetVehiclesQuery({
      limit: INITIAL_LIMIT,
    });
  const { data: allProperties, isLoading: allPropertiesIsLoading } =
    useGetPropertiesQuery({
      limit: INITIAL_LIMIT,
    });

  const [searchType, setSearchType] = useState('');

  const { country, brand, slug, tagline } = getDynamicBranding();
  const pageDescription = `A peer-to-peer boat hire and experiences platform in ${country}.`;
  const pageTitle = `Boat hire and experiences in ${country}`;

  // Signal to prerenderers/crawlers that content is ready only after initial data loads
  useEffect(() => {
    // default to not ready on mount

    (window as any).prerenderReady = false;
  }, []);

  useEffect(() => {
    const initialDataReady =
      // Either SSR delivered data or client hooks finished
      (!!initialData?.boats || (!!allBoats && !allBoatsIsLoading)) &&
      (!!initialData?.experiences ||
        (!!allExperiences && !allExperiencesIsLoading)) &&
      (!!initialData?.vehicles || (!!allVehicles && !allVehiclesIsLoading)) &&
      (!!initialData?.properties ||
        (!!allProperties && !allPropertiesIsLoading));

    if (initialDataReady) {
      (window as any).prerenderReady = true;
    }
  }, [
    initialData,
    allBoatsIsLoading,
    allBoats,
    allExperiencesIsLoading,
    allExperiences,
    allVehiclesIsLoading,
    allVehicles,
    allPropertiesIsLoading,
    allProperties,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  timer?.end('home:fetchData');
  if (typeof window === 'undefined') {
    console.warn('[SSR][Server-Timing]', timer?.summary());
  }

  // Helper to time SSR sub-blocks, safe to call on client too
  function ssrBlock<T>(name: string, render: () => T): T {
    timer?.start(`SSR:${name}`);
    try {
      return render();
    } finally {
      timer?.end(`SSR:${name}`);
    }
  }

  function renderListingSection({
    id,
    heading,
    icon,
    bookableType,
    searchTab,
    href,
    ctaLabel,
    listings,
    isLoading,
  }: ListingSection) {
    if (searchType !== '' && searchType !== searchTab) return null;
    if (!listings && isLoading) {
      return (
        <HomePageCard
          key={id}
          heading={heading}
          icon={icon}
          data-cy={`section-${id}`}
        >
          <ListingCardSkeletonGrid />
        </HomePageCard>
      );
    }
    if (!listings || listings.length === 0) return null;
    return ssrBlock(id, () => (
      <Profiler id={id} key={id} onRender={onRender}>
        <HomePageCard heading={heading} icon={icon} data-cy={`section-${id}`}>
          {listings.map((listing, index) => {
            const card = (
              <Grid
                item
                xs={12}
                sm={4}
                lg={3}
                key={`${id}-${index}`}
                sx={listingVisibilitySx(index, listings.length)}
              >
                <ListingCard
                  bookable={listing}
                  bookableType={bookableType}
                  router={router}
                  imageRenderedWidths={LISTING_CARD_IMAGE_WIDTHS}
                />
              </Grid>
            );
            return index < CARD_PROFILE_SAMPLE ? (
              <Profiler
                id={`${bookableType.toLowerCase()}-card:${index}`}
                key={`${id}-profiled-${index}`}
                onRender={onRender}
              >
                {card}
              </Profiler>
            ) : (
              card
            );
          })}
          <Grid item xs={12} sm={4} lg={3}>
            <SeeAllCard href={href}>{ctaLabel}</SeeAllCard>
          </Grid>
        </HomePageCard>
      </Profiler>
    ));
  }

  // Lighthouse flagged this as the likely LCP element: a single 2400px-wide
  // background image loaded on every device, including small mobile
  // viewports where it's rendered at a fraction of that size. Serve a
  // narrower render per breakpoint instead - background-image only fetches
  // whichever one actually matches at load time, not all three. +Head.tsx
  // preloads the same URLs with fetchpriority=high.
  const heroImage = (width: number) =>
    `url('${heroImageUrl(appConfig.APP_BRAND, width)}')`;

  const heroContent = ssrBlock('hero', () => (
    <Grid
      container
      flexDirection={'row'}
      sx={{
        flexWrap: 'wrap',
        backgroundImage: Object.fromEntries(
          heroImageSizes.map(({ breakpoint, width }) => [
            breakpoint,
            heroImage(width),
          ])
        ),
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        justifyContent: { xs: 'center', sm: 'flex-start' },
        alignItems: 'center',
        boxShadow: '0 0 10px rgba(0,0,0,0.7) inset',
        padding: { xs: 0, lg: '7em 10%' },
        maxWidth: '100%',
      }}
    >
      <ContentWrapper maxwidth={styles.maxWidth}>
        <Grid
          item
          xs={10}
          md={12}
          lg={6.2}
          xl={8}
          sx={{ margin: { xs: '5em 2em', sm: '2em 0' } }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: '2.2em',
              color: '#fff',
              textShadow:
                '0 0 20px rgba(0,0,0,0.7), 0 0 10px rgba(0,0,0,0.3), 0 0 5px rgba(0,0,0,0.7)',
              textAlign: 'left',
            }}
          >
            {tagline}
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#fff',
              textAlign: 'left',
              textShadow: '0 0 20px rgba(0,0,0,0.7),0 0 5px rgba(0,0,0,0.5)',
              marginBottom: '1em',
            }}
          >
            Stay. Sail. Experience {country}.
          </Typography>
        </Grid>
        <Grid item xs={12} md={12} lg={6.2} xl={8}>
          <Paper
            sx={{
              width: '100%',
              boxShadow: 'none',
              borderRadius: { xs: 0, lg: '20px' },
            }}
          >
            <Profiler id={'search'} onRender={onRender}>
              <HomeSearch handleSearchChange={setSearchType} />
            </Profiler>
          </Paper>
        </Grid>
      </ContentWrapper>
    </Grid>
  ));

  // Limit item-level profiling to avoid excessive logs
  const CARD_PROFILE_SAMPLE = 12;

  const profiledHero = (
    <Profiler id={'hero-container'} onRender={onRender}>
      {heroContent}
    </Profiler>
  );

  const listingSections: ListingSection[] = [
    {
      id: 'boats',
      heading: `Best Boats to Hire in ${country}`,
      icon: <SailBoat />,
      bookableType: 'Boat',
      searchTab: 'Boat',
      href: '/boats',
      ctaLabel: 'View all Boats',
      listings: initialData?.boats ?? allBoats,
      isLoading: allBoatsIsLoading,
    },
    {
      id: 'experiences',
      heading: `Best Experiences in ${country}`,
      icon: <Stars />,
      bookableType: 'Experience',
      searchTab: 'Experience',
      href: '/experiences',
      ctaLabel: 'View all Experiences',
      listings: initialData?.experiences ?? allExperiences,
      isLoading: allExperiencesIsLoading,
    },
    {
      id: 'vehicles',
      heading: `Best Vehicles to Hire in ${country}`,
      icon: <DirectionsCarOutlined />,
      bookableType: 'Vehicle',
      searchTab: 'Experience',
      href: '/vehicles',
      ctaLabel: 'View all Vehicles',
      listings: initialData?.vehicles ?? allVehicles,
      isLoading: allVehiclesIsLoading,
    },
    {
      id: 'properties',
      heading: `Best Accommodation in ${country}`,
      icon: <HomeOutlined />,
      bookableType: 'Property',
      searchTab: 'Property',
      href: '/properties',
      ctaLabel: 'View all Properties',
      listings: initialData?.properties ?? allProperties,
      isLoading: allPropertiesIsLoading,
    },
  ];

  return (
    <Profiler id={'pageWithHeader'} onRender={onRender}>
      <PageWithHeader
        contentMargin={false}
        heroContent={profiledHero}
        maxWidth={styles.maxWidth}
        showCurrencyConvertor={true}
        showReassurance={true}
      >
        <PageMeta title={pageTitle} description={pageDescription} />
        {listingSections.map((section) => renderListingSection(section))}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Profiler id={'about'} onRender={onRender}>
              <FeatureList
                title={'A peer-to-peer boat hire platform with a purpose.'}
                list={false}
              >
                <BodyText>
                  {brand} is a boat rental platform that aims to simplify boat
                  management for operators and provide an easy checkout for
                  guests hiring boats in {country}. Our low margins on rental
                  bookings offers our boat owners and guests the best value for
                  money. We adhere to international standards of consumer
                  protection law for guests giving them total peace of mind that
                  if things go wrong, we are here to resolve the issue fairly.
                </BodyText>
                <BodyText>
                  We verify every owner and their boats and experiences on the
                  platform to ensure that you get exactly what you have paid for
                  without surprises.
                </BodyText>
                <BodyText>
                  Every hire helps the environment! We are dedicated to keeping
                  the oceans clean and 10% of our service charge is set aside
                  for local charitable causes preserving the pristine marine
                  environment in {country}.
                </BodyText>
              </FeatureList>
            </Profiler>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Profiler id={'features-guests'} onRender={onRender}>
              <FeatureList title={'Guests'}>
                <FeatureListItem
                  icon={<Search />}
                  text={`Search our listings for your ideal nautical experience. Find the best deals on
                motorboat or sailboat rental in ${country} and take advantage of our safe and
                easy checkout to remove the hassle from booking your experience.`}
                />
                <FeatureListItem
                  text="Chat directly with boat owners, who will be happy to answer your queries to make
                your experience unforgettable."
                  icon={<Chat />}
                />
                <FeatureListItem
                  text="Book directly online using our smooth, easy to use platform. Your card will only
                be charged once the owner accepts your booking and we will hold your funds until your
                booking is complete, giving you complete peace of mind."
                  icon={<Sailing />}
                />
              </FeatureList>
            </Profiler>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Profiler id={'features-owners'} onRender={onRender}>
              <FeatureList title={'Owners'}>
                <FeatureListItem
                  text="You can create and manage a boat or experience listing free of charge on the
                platform. Whether you are offering fishing excursions, diving trips or private charter
                with customised itinerary, you can use our modern, easy to use platform to create the
                perfect listing."
                  icon={<Sailing />}
                />
                <FeatureListItem
                  text="You set the price and have complete control over who rents your boat or participates
                in experience you offer. We provide an iCal feed so that you can manage your boat
                availability from your own calendar and provide tools to communicate with your guests."
                  icon={<Rule />}
                />
                <FeatureListItem
                  text="You benefit from the lowest commission in the industry, ensuring that your offering
                gives the best value for money to your guests."
                  icon={<AttachMoney />}
                />
                <FeatureListItem
                  text=" We pay directly into your account, anywhere in the world."
                  icon={<AccountBalance />}
                />
              </FeatureList>
            </Profiler>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Profiler id={'features-destinations'} onRender={onRender}>
              <FeatureList title={'Destinations'}>
                <FeatureListItem
                  text={country}
                  icon={<LocationOn />}
                  href={`/destinations/${slug}`}
                />
              </FeatureList>
            </Profiler>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Profiler id={'features-experiences'} onRender={onRender}>
              <FeatureList title={'Experiences'}>
                <FeatureListItem
                  text="Diving"
                  icon={<ScubaDiving />}
                  href="/experiences/diving"
                />
                <FeatureListItem
                  text="Swimming"
                  icon={<Pool />}
                  href="/experiences/swimming"
                />
                <FeatureListItem
                  text="Sailing"
                  icon={<Sailing />}
                  href="/experiences/sailing"
                />
                <FeatureListItem
                  text="Fishing"
                  icon={<Phishing />}
                  href="/experiences/fishing"
                />
              </FeatureList>
            </Profiler>
          </Grid>
        </Grid>
      </PageWithHeader>
    </Profiler>
  );
};

export default withSsrTiming(HomePage, 'HomePage');
